<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="model" :validate-on="['submit']" class="h-full">
            <UCard class="h-full flex flex-col overflow-y-auto">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">New Broadcast</h3>
                    </div>
                </template>

                <div class="space-y-6">
                    <UFormGroup name="title" label="Title" required>
                        <UInput v-model="model.title" type="text" size="xl" />
                    </UFormGroup>

                    <UFormGroup name="segment_id" label="Target Segment" required>
                        <USelectMenu
                            v-model="selectedSegment"
                            :options="segments"
                            option-attribute="name"
                            searchable
                            :search-attributes="['name', 'description']"
                            placeholder="Select a segment..."
                            size="xl"
                            :loading="loadingSegments"
                        >
                            <template #option="{ option }">
                                <div class="py-1">
                                    <p class="font-medium">{{ option.name }}</p>
                                    <p v-if="option.description" class="text-xs text-gray-500">{{ option.description }}</p>
                                </div>
                            </template>
                        </USelectMenu>
                        <div v-if="!selectedSegment" class="mt-3 relative block w-full rounded-lg border border-dashed border-gray-300 p-6 text-center">
                            <UIcon name="i-ph-users-three-duotone" class="mx-auto size-6 text-gray-400" />
                            <h3 class="mt-2 text-sm font-medium text-gray-900">No segment selected</h3>
                            <p class="mt-1 text-xs text-gray-500">
                                <NuxtLink to="/broadcasts/segments" class="text-blue-600 hover:underline">Create a segment</NuxtLink>
                                to target specific customers
                            </p>
                        </div>
                    </UFormGroup>

                    <UFormGroup name="template_id" label="Template" required>
                        <USelectMenu
                            v-model="selectedTemplate"
                            :options="sendableTemplates"
                            option-attribute="name"
                            searchable
                            placeholder="Select a template..."
                            size="xl"
                            :loading="loadingTemplates"
                        >
                            <template #option="{ option }">
                                <div class="py-1 flex items-center gap-2">
                                    <UBadge color="gray" variant="subtle" size="xs">{{ option.channel }}</UBadge>
                                    <span class="font-medium">{{ option.name }}</span>
                                    <span class="text-xs text-gray-500">v{{ option.version }}</span>
                                </div>
                            </template>
                        </USelectMenu>
                        <div v-if="!selectedTemplate" class="mt-3 relative block w-full rounded-lg border border-dashed border-gray-300 p-6 text-center">
                            <UIcon name="i-ph-file-text-duotone" class="mx-auto size-6 text-gray-400" />
                            <h3 class="mt-2 text-sm font-medium text-gray-900">No template selected</h3>
                            <p class="mt-1 text-xs text-gray-500">
                                <NuxtLink to="/broadcasts/templates" class="text-blue-600 hover:underline">Create a template</NuxtLink>
                                to define the message
                            </p>
                        </div>
                    </UFormGroup>

                    <div>
                        <UButton
                            @click="fetchEstimate"
                            :loading="estimateLoading"
                            :disabled="!model.segment_id"
                            color="gray"
                            variant="outline"
                            icon="i-ph-calculator-duotone"
                        >
                            Review recipients &amp; cost
                        </UButton>

                        <div v-if="estimate" class="mt-3 rounded-lg border border-gray-200 p-4 space-y-3">
                            <div class="grid grid-cols-3 gap-3 text-center">
                                <div>
                                    <p class="text-lg font-semibold text-gray-900">{{ estimate.eligible.toLocaleString() }}</p>
                                    <p class="text-xs text-gray-500">Eligible</p>
                                </div>
                                <div>
                                    <p class="text-lg font-semibold text-amber-600">{{ estimate.suppressed.toLocaleString() }}</p>
                                    <p class="text-xs text-gray-500">Suppressed</p>
                                </div>
                                <div>
                                    <p class="text-lg font-semibold text-red-500">{{ estimate.ineligible.toLocaleString() }}</p>
                                    <p class="text-xs text-gray-500">Ineligible</p>
                                </div>
                            </div>
                            <div class="flex items-center justify-between text-sm border-t border-gray-100 pt-3">
                                <span class="text-gray-500">Estimated cost</span>
                                <span class="font-medium">{{ estimate.currency }} {{ estimate.totalCost.toFixed(4) }}</span>
                            </div>
                            <div v-if="estimate.byCountry && Object.keys(estimate.byCountry).length" class="space-y-1">
                                <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">By country</p>
                                <div v-for="(v, code) in estimate.byCountry" :key="code" class="flex items-center justify-between text-sm">
                                    <span class="text-gray-700">{{ code }}</span>
                                    <span class="text-gray-500">{{ v.count }} · {{ estimate.currency }} {{ v.cost.toFixed(4) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <UFormGroup label="Schedule">
                        <div class="flex items-center gap-3">
                            <UToggle v-model="scheduling" />
                            <span class="text-sm text-gray-600">{{ scheduling ? 'Schedule for later' : 'Send immediately on publish' }}</span>
                        </div>
                        <UInput
                            v-if="scheduling"
                            v-model="model.scheduled_at"
                            type="datetime-local"
                            size="xl"
                            class="mt-3"
                        />
                    </UFormGroup>
                </div>

                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">Cancel</UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="gray">Save Draft</UButton>
                        <UButton @click="saveAndPublish" size="lg" :loading="publishing" color="black" :disabled="!canPublish">
                            Save &amp; Publish
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { BroadcastSegment } from '~/types/segment'
import type { EstimateResult } from '~/types/broadcast'
import type { Template } from '~/types/template'
import { isSendable } from '~/utils/template'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const client = useSupabaseClient()
const authStore = useAuthStore()

const form = ref()
const loading = ref(false)
const publishing = ref(false)
const loadingSegments = ref(false)
const estimateLoading = ref(false)
const segments = ref<BroadcastSegment[]>([])
const selectedSegment = ref<BroadcastSegment | null>(null)
const estimate = ref<EstimateResult | null>(null)
const scheduling = ref(false)

const templates = ref<Template[]>([])
const loadingTemplates = ref(false)
const selectedTemplate = ref<Template | null>(null)

const sendableTemplates = computed(() => templates.value.filter(isSendable))

const model = ref({
    title: '',
    segment_id: null as string | null,
    template_id: null as string | null,
    scheduled_at: null as string | null,
})

const schema = z.object({
    title: z.string().min(1),
    segment_id: z.string().nullable(),
    template_id: z.string(),
    scheduled_at: z.string().nullable(),
})

const canPublish = computed(() => {
    return !!model.value.segment_id && !!model.value.template_id
})

watch(() => props.open, async (open) => {
    if (open) {
        model.value = {
            title: '',
            segment_id: null,
            template_id: null,
            scheduled_at: null,
        }
        selectedSegment.value = null
        selectedTemplate.value = null
        estimate.value = null
        scheduling.value = false
        await fetchSegments()
    }
})

watch(selectedSegment, (segment) => {
    model.value.segment_id = segment?.id ?? null
    estimate.value = null
})

watch(selectedTemplate, (t) => {
    model.value.template_id = t?.id ?? null
    estimate.value = null
})

watch(scheduling, (on) => {
    if (!on) model.value.scheduled_at = null
})

onMounted(async () => {
    loadingTemplates.value = true
    try {
        templates.value = await $fetch<Template[]>('/api/templates')
    } finally {
        loadingTemplates.value = false
    }
})

async function fetchSegments() {
    loadingSegments.value = true
    const { data } = await client
        .from('broadcast_segments')
        .select('*')
        .order('updated_at', { ascending: false })
        .returns<BroadcastSegment[]>()
    if (data) segments.value = data
    loadingSegments.value = false
}

async function fetchEstimate() {
    if (!model.value.segment_id) return
    estimateLoading.value = true
    try {
        estimate.value = await $fetch('/api/broadcasts/estimate', {
            method: 'POST',
            body: { template_id: model.value.template_id, segment_id: model.value.segment_id },
        }) as EstimateResult
    } catch (e) {
        console.error('Estimate error:', e)
    }
    estimateLoading.value = false
}

async function insertBroadcast() {
    const row = {
        title: model.value.title,
        segment_id: model.value.segment_id,
        template_id: model.value.template_id,
        scheduled_at: model.value.scheduled_at || null,
        status: 'draft',
        created_by: authStore.user?.id,
    }
    const { data, error } = await client
        .from('broadcasts')
        .insert(row as never)
        .select('id')
        .single()
    if (error) throw error
    return data
}

async function onSubmit(_event: FormSubmitEvent<any>) {
    loading.value = true
    try {
        await insertBroadcast()
        dismiss(true)
    } catch (e) {
        console.error(e)
    }
    loading.value = false
}

async function saveAndPublish() {
    if (!canPublish.value) return
    try {
        await form.value.validate()
    } catch {
        return
    }
    publishing.value = true
    try {
        const row = await insertBroadcast()
        await $fetch('/api/broadcasts/publish', {
            method: 'POST',
            body: { id: row.id },
        })
        dismiss(true)
    } catch (e) {
        console.error(e)
    }
    publishing.value = false
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) emit('reload', true)
}
</script>
